import type { Element as SlateElement, Editor } from 'slate';
import { Transforms } from 'slate';
import type { ChatGroupMember } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import type {
  DeserializePayloadRender,
  SerializePayloadOptions,
} from '../../../chat-input/helpers';
import type { MentionEditor } from './types';
import { MentionElementType, type MentionElement } from './types';
import MentionMessage from './components/mention-message';

export function isMentionElement(element: SlateElement) {
  return element.type === MentionElementType;
}

export function insertMention(editor: Editor, user: ChatGroupMember) {
  const mention: MentionElement = {
    type: MentionElementType,
    user,
    children: [{ text: '' }],
  };
  Transforms.insertNodes(editor, mention);
  Transforms.move(editor);
}

export function withMentionEditor(editor: Editor): MentionEditor {
  const { isInline, isVoid, markableVoid } = editor;

  editor.isInline = (element: SlateElement) => {
    return isMentionElement(element) || isInline(element);
  };

  editor.isVoid = (element: SlateElement) => {
    return isMentionElement(element) || isVoid(element);
  };

  editor.markableVoid = (element: SlateElement) => {
    return isMentionElement(element) || markableVoid(element);
  };

  return Object.assign(editor, {
    insertMention(user: ChatGroupMember) {
      return insertMention(editor, user);
    },
  });
}

export interface SerializePayloadOptions_mention extends SerializePayloadOptions {
  mentionIds: string[];
}

export function serializePayload_mention({
  entry: [node],
  callback,
  mentionIds,
}: SerializePayloadOptions_mention) {
  if (isMentionElement(node as MentionElement)) {
    const {
      user: { user_id, name },
    } = node as MentionElement;

    if (!mentionIds.includes(user_id)) {
      mentionIds.push(user_id);
    }

    callback({
      part: `@${name}`,
      entity: {
        type: MentionElementType,
        user: {
          user_id,
          name,
        },
      },
    });

    return true;
  }
}

export const deserializePayloadRender_mention: DeserializePayloadRender = ({
  type,
  entity,
  msg,
  key,
}) => {
  if (type === MentionElementType) {
    return {
      children: (
        <MentionMessage key={key} user={entity.user} msg={msg}></MentionMessage>
      ),
    };
  }
};
